import { useEffect } from "react";

import { playSfx, unlockSfx, type Sfx } from "@/platform";

/**
 * One document-level listener set gives every control its retro sound, so no
 * component has to remember to call one.
 *
 * - mouse hover onto a control → `hover`
 * - click → `confirm`; switches/tabs/radios → `toggleOn`/`toggleOff`
 * - Escape → `back`; typing in a field → `key`
 *
 * A control (or any ancestor) can override with `data-sfx="none|back|delete|…"`.
 * A checkbox being ticked is left silent here: the task tick plays its own
 * completion sound from the mutation, and two sounds at once read as noise.
 */
const CONTROL =
  'button, a[href], summary, select, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="option"], [role="tab"], [role="switch"], [role="checkbox"], [role="radio"], input[type="checkbox"], input[type="radio"]';

const HOVER_GAP_MS = 35;

function controlOf(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element ? (target.closest(CONTROL) as HTMLElement | null) : null;
}

function isDisabled(el: HTMLElement): boolean {
  return (el as HTMLButtonElement).disabled === true || el.getAttribute("aria-disabled") === "true";
}

function override(el: Element): string | null {
  return el.closest("[data-sfx]")?.getAttribute("data-sfx") ?? null;
}

function clickSound(el: HTMLElement): Sfx | null {
  const forced = override(el);
  if (forced) {
    return forced === "none" ? null : (forced as Sfx);
  }
  const role = el.getAttribute("role");
  const input = el instanceof HTMLInputElement ? el : null;
  if (role === "checkbox" || input?.type === "checkbox") {
    // Fires before the state flips: "false" now means it is about to be ticked.
    const checked = input ? input.checked : el.getAttribute("aria-checked") === "true";
    return checked ? "toggleOff" : null;
  }
  if (role === "switch") {
    return el.getAttribute("aria-checked") === "true" ? "toggleOff" : "toggleOn";
  }
  if (role === "tab" || role === "radio" || role === "menuitemradio" || input?.type === "radio") {
    return "toggleOn";
  }
  return "confirm";
}

function isTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable || target instanceof HTMLTextAreaElement) {
    return true;
  }
  return target instanceof HTMLInputElement && !["checkbox", "radio", "button", "submit", "range", "color", "file"].includes(target.type);
}

export function useUiSounds(): void {
  useEffect(() => {
    let hovered: HTMLElement | null = null;
    let lastHover = 0;

    const onPointerOver = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") {
        return;
      }
      const el = controlOf(event.target);
      if (el === hovered) {
        return;
      }
      hovered = el;
      if (!el || isDisabled(el) || override(el) === "none") {
        return;
      }
      const now = performance.now();
      if (now - lastHover < HOVER_GAP_MS) {
        return;
      }
      lastHover = now;
      playSfx("hover");
    };

    const onPointerDown = () => unlockSfx();

    // Capture phase: the state attributes are read before React flips them.
    const onClick = (event: MouseEvent) => {
      const el = controlOf(event.target);
      if (!el || isDisabled(el)) {
        return;
      }
      const sound = clickSound(el);
      if (sound) {
        playSfx(sound);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      unlockSfx();
      if (event.key === "Escape") {
        playSfx("back");
        return;
      }
      if (event.isComposing || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (isTextField(event.target) && (event.key.length === 1 || event.key === "Backspace" || event.key === "Enter")) {
        playSfx("key");
      }
    };

    document.addEventListener("pointerover", onPointerOver, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerover", onPointerOver, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);
}
